import type { CascaderOption } from '../types';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface CascaderInternalState<
  OptionType extends CascaderOption = CascaderOption,
> {
  inputValue: string;
  selectedPath: string[];
  cursorPath: number[];
  highlightedValue: string | null;
  isOpen: boolean;
  hoverOption: OptionType | null;
  lastInteractedType: 'cursor' | 'hover' | null;
}

export interface CascaderInitOptions {
  value?: string[];
}

export const createInitialState = <
  OptionType extends CascaderOption = CascaderOption,
>(
  opts: CascaderInitOptions = {},
): CascaderInternalState<OptionType> => ({
  inputValue: '',
  selectedPath: opts.value ?? [],
  cursorPath: [],
  highlightedValue: null,
  isOpen: false,
  hoverOption: null,
  lastInteractedType: null,
});

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type CascaderAction<OptionType extends CascaderOption = CascaderOption> =

    | { type: 'OPEN'; indexPath: number[] }
    | { type: 'CLOSE' }
    | { type: 'SET_INPUT'; value: string }
    | {
        type: 'SELECT';
        fullPath: string[];
        indexPath: number[];
      }
    | { type: 'CLEAR' }
    | { type: 'SET_CURSOR_PATH'; path: number[] }
    | { type: 'SET_HIGHLIGHTED_VALUE'; value: string | null }
    | { type: 'SET_HOVER'; option: OptionType | null }
    | { type: 'NAVIGATE_TO_INDEX'; level: number; index: number }
    | { type: 'NAVIGATE_PARENT'; level: number }
    | { type: 'NAVIGATE_CHILD' }
    | {
        type: 'DRILL_INTO_SUBMENU';
        level: number;
        optionIndex: number;
      };

// ---------------------------------------------------------------------------
// Action creators
// ---------------------------------------------------------------------------

export const cascaderActions = {
  open: <OptionType extends CascaderOption = CascaderOption>(
    indexPath: number[],
  ): CascaderAction<OptionType> => ({
    type: 'OPEN',
    indexPath,
  }),
  close: <
    OptionType extends CascaderOption = CascaderOption,
  >(): CascaderAction<OptionType> => ({
    type: 'CLOSE',
  }),
  setInput: <OptionType extends CascaderOption = CascaderOption>(
    value: string,
  ): CascaderAction<OptionType> => ({
    type: 'SET_INPUT',
    value,
  }),
  select: <OptionType extends CascaderOption = CascaderOption>(
    fullPath: string[],
    indexPath: number[],
  ): CascaderAction<OptionType> => ({
    type: 'SELECT',
    fullPath,
    indexPath,
  }),
  clear: <
    OptionType extends CascaderOption = CascaderOption,
  >(): CascaderAction<OptionType> => ({
    type: 'CLEAR',
  }),
  setCursorPath: <OptionType extends CascaderOption = CascaderOption>(
    path: number[],
  ): CascaderAction<OptionType> => ({
    type: 'SET_CURSOR_PATH',
    path,
  }),
  setHighlightedValue: <OptionType extends CascaderOption = CascaderOption>(
    value: string | null,
  ): CascaderAction<OptionType> => ({
    type: 'SET_HIGHLIGHTED_VALUE',
    value,
  }),
  setHover: <OptionType extends CascaderOption = CascaderOption>(
    option: OptionType | null,
  ): CascaderAction<OptionType> => ({
    type: 'SET_HOVER',
    option,
  }),
  navigateToIndex: <OptionType extends CascaderOption = CascaderOption>(
    level: number,
    index: number,
  ): CascaderAction<OptionType> => ({
    type: 'NAVIGATE_TO_INDEX',
    level,
    index,
  }),
  navigateParent: <OptionType extends CascaderOption = CascaderOption>(
    level: number,
  ): CascaderAction<OptionType> => ({
    type: 'NAVIGATE_PARENT',
    level,
  }),
  navigateChild: <
    OptionType extends CascaderOption = CascaderOption,
  >(): CascaderAction<OptionType> => ({ type: 'NAVIGATE_CHILD' }),
  drillIntoSubmenu: <OptionType extends CascaderOption = CascaderOption>(
    level: number,
    optionIndex: number,
  ): CascaderAction<OptionType> => ({
    type: 'DRILL_INTO_SUBMENU',
    level,
    optionIndex,
  }),
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export const cascaderReducer = <
  OptionType extends CascaderOption = CascaderOption,
>(
  state: CascaderInternalState<OptionType>,
  action: CascaderAction<OptionType>,
): CascaderInternalState<OptionType> => {
  switch (action.type) {
    case 'OPEN':
      return {
        ...state,
        isOpen: true,
        cursorPath: action.indexPath,
        lastInteractedType: 'cursor',
      };

    case 'CLOSE':
      if (!state.isOpen && state.inputValue.length === 0) return state;
      return {
        ...state,
        inputValue: '',
        isOpen: false,
        highlightedValue: null,
        cursorPath: [],
      };

    case 'SET_INPUT': {
      const wasSearchMode = state.inputValue.length > 0;
      const isSearchMode = action.value.length > 0;
      const enteringSearch = !wasSearchMode && isSearchMode;
      const exitingSearch = wasSearchMode && !isSearchMode;

      return {
        ...state,
        inputValue: action.value,
        isOpen: enteringSearch ? true : state.isOpen,
        highlightedValue: exitingSearch ? null : state.highlightedValue,
      };
    }

    case 'SELECT':
      return {
        ...state,
        selectedPath: action.fullPath,
        cursorPath: action.indexPath,
        inputValue: '',
        isOpen: false,
        lastInteractedType: 'cursor',
      };

    case 'CLEAR':
      return {
        ...state,
        selectedPath: [],
        inputValue: '',
        cursorPath: [],
        highlightedValue: null,
        isOpen: false,
        hoverOption: null,
        lastInteractedType: null,
      };

    case 'SET_CURSOR_PATH':
      return { ...state, cursorPath: action.path };

    case 'SET_HIGHLIGHTED_VALUE':
      return { ...state, highlightedValue: action.value };

    case 'SET_HOVER':
      return {
        ...state,
        hoverOption: action.option,
        lastInteractedType: 'hover',
      };

    case 'NAVIGATE_TO_INDEX': {
      const newCursorPath = [...state.cursorPath];
      if (action.level < state.cursorPath.length) {
        newCursorPath[action.level] = action.index;
      } else if (action.level === 0 && state.cursorPath.length === 0) {
        return {
          ...state,
          cursorPath: [action.index],
          lastInteractedType: 'cursor',
        };
      }
      return {
        ...state,
        cursorPath: newCursorPath,
        lastInteractedType: 'cursor',
      };
    }

    case 'NAVIGATE_PARENT': {
      if (action.level > 0) {
        return {
          ...state,
          cursorPath: state.cursorPath.slice(0, action.level),
          lastInteractedType: 'cursor',
        };
      }
      // At root level, navigating to parent closes
      return {
        ...state,
        isOpen: false,
        highlightedValue: null,
        cursorPath: [],
      };
    }

    case 'NAVIGATE_CHILD':
      return {
        ...state,
        cursorPath: [...state.cursorPath, 0],
        lastInteractedType: 'cursor',
      };

    case 'DRILL_INTO_SUBMENU': {
      const drillPath = state.cursorPath.slice(0, action.level + 1);
      drillPath[action.level] = action.optionIndex;
      return {
        ...state,
        cursorPath: drillPath,
        lastInteractedType: 'cursor',
      };
    }

    default:
      return state;
  }
};
